///
/// EarthBrowser
///
/// Created by Matt Giger on December 23, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { logger } from "@/utils/LogDispatcher"

export type LocalDataFileType = "geopackage" | "csv" | "shapefile" | "zip"

export interface LocalDataFile {
  name: string
  path: string
  type: LocalDataFileType
  size: number
  lastModified: number
  handle: FileSystemFileHandle
}

export interface ScanResult {
  files: LocalDataFile[]
  scannedAt: number
  totalFiles: number
  errors: string[]
}

/**
 * Service for scanning a local directory for data files
 */
export class LocalDataScanner {
  private static readonly SUPPORTED_EXTENSIONS = new Map<string, LocalDataFileType>([
    [".gpkg", "geopackage"],
    [".csv", "csv"],
    [".shp", "shapefile"],
    [".zip", "zip"],
  ])

  private static readonly MAX_DEPTH = 5 // Prevent infinite recursion

  /**
   * Scan a directory for supported data files
   */
  static async scanDirectory(
    directoryHandle: FileSystemDirectoryHandle,
    options: {
      recursive?: boolean
      maxDepth?: number
    } = {}
  ): Promise<ScanResult> {
    const { recursive = true, maxDepth = this.MAX_DEPTH } = options
    const files: LocalDataFile[] = []
    const errors: string[] = []
    const startTime = Date.now()

    try {
      await this.scanDirectoryRecursive(
        directoryHandle,
        "",
        files,
        errors,
        recursive ? maxDepth : 0,
        0
      )

      logger.info(`Scanned directory: found ${files.length} files in ${Date.now() - startTime}ms`)

      return {
        files,
        scannedAt: Date.now(),
        totalFiles: files.length,
        errors,
      }
    } catch (error) {
      logger.error("Failed to scan directory", error)
      errors.push(`Failed to scan directory: ${error}`)

      return {
        files,
        scannedAt: Date.now(),
        totalFiles: files.length,
        errors,
      }
    }
  }

  /**
   * Recursively scan a directory
   */
  private static async scanDirectoryRecursive(
    directoryHandle: FileSystemDirectoryHandle,
    currentPath: string,
    files: LocalDataFile[],
    errors: string[],
    maxDepth: number,
    currentDepth: number
  ): Promise<void> {
    if (currentDepth > maxDepth) {
      return
    }

    try {
      // Iterate through directory entries using async iterator
      // The iterator returns [name, handle] tuples
      // @ts-ignore - FileSystemDirectoryHandle is async iterable but TypeScript may not recognize it
      for await (const [name, entry] of directoryHandle) {
        const entryPath = currentPath ? `${currentPath}/${name}` : name

        try {
          if (entry.kind === "file") {
            // Check if file has supported extension
            const fileType = this.getFileType(name)
            if (fileType) {
              const file = await entry.getFile()

              files.push({
                name,
                path: entryPath,
                type: fileType,
                size: file.size,
                lastModified: file.lastModified,
                handle: entry,
              })
            }
          } else if (entry.kind === "directory") {
            // Recursively scan subdirectory
            await this.scanDirectoryRecursive(
              entry,
              entryPath,
              files,
              errors,
              maxDepth,
              currentDepth + 1
            )
          }
        } catch (error) {
          errors.push(`Failed to process ${entryPath}: ${error}`)
          logger.warn(`Failed to process ${entryPath}`, error)
        }
      }
    } catch (error) {
      errors.push(`Failed to read directory ${currentPath}: ${error}`)
      logger.error(`Failed to read directory ${currentPath}`, error)
    }
  }

  /**
   * Get file type based on extension
   */
  private static getFileType(filename: string): LocalDataFileType | null {
    const lowerName = filename.toLowerCase()

    for (const [ext, type] of this.SUPPORTED_EXTENSIONS.entries()) {
      if (lowerName.endsWith(ext)) {
        return type
      }
    }

    return null
  }

  /**
   * Check if a filename is supported
   */
  static isSupportedFile(filename: string): boolean {
    return this.getFileType(filename) !== null
  }

  /**
   * Get file extension from filename
   */
  static getFileExtension(filename: string): string {
    const lastDot = filename.lastIndexOf(".")
    return lastDot >= 0 ? filename.slice(lastDot).toLowerCase() : ""
  }
}

